import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, CheckIcon, StoreIcon, VideoIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { DeleteButton } from '../ui/DeleteButton';
import { SlotPicker } from '../../ui/SlotPicker';
import { Spinner } from '../../ui/Spinner';
import { useAdmin } from '../AdminContext';
import { btnGhost, btnPrimary, btnSecondary } from '../ui/styles';
import { useNow } from '../../hooks/useNow';
import { hasStarted } from '../../core/rules/agenda';
import { dayLong } from '../../core/format/dates';
import { ADMIN_BASE } from '../../utils/hosts';
import type { Appointment } from '../../core/types/account';
import type { CustomerFileControl } from './useCustomerFile';

/** Eén afspraak in het dossier, met bevestigen, afronden, verzetten en annuleren. */
export function AppointmentRow({
  appt,
  ctl
}: {
  appt: Appointment;
  ctl: CustomerFileControl;
}) {
  const {
    index,
    site
  } = useAdmin();
  const [picking, setPicking] = useState(false);
  const t = index.treatmentBySlug(appt.treatmentSlug);
  const course = ctl.file.account.course;
  // Zelfde moment als in de agenda: afronden kan vanaf de begintijd, niet al 's ochtends.
  const now = useNow();
  const agendaHref = `${ADMIN_BASE}/agenda?datum=${appt.date}&afspraak=${encodeURIComponent(appt.id)}`;
  const Icon = appt.kind === 'video' ? VideoIcon : StoreIcon;
  // Tijdens het bewaren staan alle knoppen van deze afspraak uit: geen tweede "Afronden".
  const busy = ctl.isBusy(appt.id);
  return <li className="px-4 py-4 sm:px-5" data-id="element-993" data-mp="1niutyfm3mthn">
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3" data-id="element-994" data-mp="1nm0jfvyn3xdw">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sand text-ink" aria-hidden="true" data-id="element-995" data-mp="1skbto7suk9wn">
          <Icon className="h-4 w-4" strokeWidth={1.5} data-id="element-996" data-mp="1wjsx6vopqwz6" />
        </span>
        <span className="min-w-0 flex-1" data-id="element-997" data-mp="3ubf2qsn39gf3">
          <span className="flex flex-wrap items-center gap-2" data-id="element-998" data-mp="3ucq7ovs9su7s">
            <span className="text-[14px] text-ink" data-id="element-999" data-mp="27co7n4j94qm">
              {t?.name ?? appt.treatmentSlug}
              <span className="sr-only" data-id="element-1000" data-mp="15ia0npm31cny">{appt.kind === 'video' ? ' (videogesprek)' : ' (in de salon)'}</span>
            </span>
            {appt.status === 'aanvraag' ? <Badge tone="accent" data-id="element-1001" data-mp="1eif4xqvzbuxw">{appt.request === 'verzet' ? 'Wil verzetten' : 'Aanvraag'}</Badge> : <Badge tone="soft" data-id="element-1002" data-mp="1afs9zh40htwh">Bevestigd</Badge>}
          </span>
          <span className="mt-0.5 block text-[13px] capitalize text-muted" data-id="element-1003" data-mp="1ofkwt1cf3pz6">
            {dayLong(appt.date)} om {appt.time}
            {appt.session && course ? ` · sessie ${appt.session} van ${course.total}` : ''}
          </span>
        </span>
      </div>

      {picking ? <div className="mt-3 sm:ml-[3.25rem]" data-id="element-1004" data-mp="1t7bbfjie0zwe">
          <SlotPicker hours={site.openingHours} confirmLabel={when => `Voorstellen: ${when}`} cancelLabel="Annuleer" busy={busy} onCancel={() => setPicking(false)} onConfirm={async (date, time) => {
        if (await ctl.proposeAppointment(appt.id, date, time)) setPicking(false);
      }} data-id="element-1005" data-mp="1t7l3dcmzth4q" />
        </div> : <div className="mt-3 flex flex-wrap items-center gap-2 sm:ml-[3.25rem]" data-id="element-1006" data-mp="2qrup0dsmkes1">
          {appt.status === 'aanvraag' && <button type="button" onClick={() => ctl.confirmAppointment(appt.id)} disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-1007" data-mp="2guga4imcny1u">
              {busy ? <Spinner data-id="element-1008" data-mp="2329ucxn3zt2x" /> : <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-1009" data-mp="231gxqphv6jyi" />}
              Bevestigen
            </button>}
          {/* Afronden op één plek: in de agenda, waar ook gebruikte producten en advies worden vastgelegd. */}
          {appt.status === 'bevestigd' && hasStarted(appt, now) ? <Link to={agendaHref} className={btnPrimary} data-id="element-1010" data-mp="1y1vsxyg0jhdv">
              <CheckIcon className="h-4 w-4" strokeWidth={2} aria-hidden="true" data-id="element-1011" data-mp="1k58prdxgvuzi" />
              Afronden in agenda
            </Link> : <Link to={agendaHref} className={btnGhost} data-id="element-1012" data-mp="1p53gnhh8skkk">
              <CalendarDaysIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1013" data-mp="1h19l9ql1jwoi" />
              In agenda
            </Link>}
          <button type="button" onClick={() => setPicking(true)} disabled={busy} className={btnSecondary} data-id="element-1014" data-mp="17crodkfvu9q5">
            {appt.status === 'aanvraag' ? 'Ander tijdstip' : 'Verzetten'}
          </button>
          <DeleteButton label="Annuleren" warning="Klant krijgt een bericht" onConfirm={() => ctl.removeAppointment(appt.id)} data-id="element-1015" data-mp="yc37mhz3tv9k" />
        </div>}
    </li>;
}